import { useState, useEffect, useCallback } from 'react';
import { Connector, PartnerDocument } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToChannelPartners,
  addChannelPartner,
  updateChannelPartner,
  deleteChannelPartner,
  uploadPartnerDocument,
  deletePartnerDocument,
  togglePartnerFavorite,
} from '@/services/channelPartnerService';

export function useChannelPartners() {
  const { user } = useAuth();
  const [partners, setPartners] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to channel partners for the current user
  useEffect(() => {
    if (!user?.id) {
      setPartners([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToChannelPartners(
      user.id,
      (updatedPartners) => {
        setPartners(updatedPartners);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  // Add a new channel partner
  const addPartner = useCallback(
    async (partner: Omit<Connector, 'id'>): Promise<Connector> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to add a channel partner');
      }

      try {
        const newPartner = await addChannelPartner(partner, user.id);
        return newPartner;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add channel partner';
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Update an existing channel partner
  const updatePartner = useCallback(
    async (partnerId: string, updates: Partial<Connector>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to update a channel partner');
      }

      try {
        // If marking a visit, add lastVisitedBy field
        if (updates.lastVisitDate && !updates.lastVisitedBy) {
          updates.lastVisitedBy = user.userId;
        }
        await updateChannelPartner(partnerId, updates);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update channel partner';
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Delete a channel partner
  const deletePartner = useCallback(
    async (partnerId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to delete a channel partner');
      }

      try {
        const partner = partners.find(p => p.id === partnerId);
        if (!partner) {
          throw new Error('Channel partner not found');
        }

        await deleteChannelPartner(partnerId, partner);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete channel partner';
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id, partners]
  );

  // Upload a document for a channel partner
  const uploadDocument = useCallback(
    async (file: File, partnerId?: string): Promise<PartnerDocument> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to upload a document');
      }

      try {
        return await uploadPartnerDocument(file, user.id, partnerId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Delete a document
  const deleteDocument = useCallback(
    async (fileUrl: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to delete a document');
      }

      try {
        await deletePartnerDocument(fileUrl);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete document';
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (partnerId: string, isFavorite: boolean): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to toggle favorite');
      }

      try {
        await togglePartnerFavorite(partnerId, isFavorite);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to toggle favorite';
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Get a single partner by ID
  const getPartner = useCallback(
    (partnerId: string): Connector | undefined => {
      return partners.find(p => p.id === partnerId);
    },
    [partners]
  );

  return {
    partners,
    loading,
    error,
    addPartner,
    updatePartner,
    deletePartner,
    uploadDocument,
    deleteDocument,
    toggleFavorite,
    getPartner,
  };
}
